import React, { forwardRef } from 'react';
import PT from 'prop-types';
import map from 'lodash/map';
import {
  FormControl,
  FormHelperText,
  Select as MuiSelect,
  MenuItem,
  styled
} from 'components';
import Input from '../Input';
import { MdExpandMore } from '../../icons';

const PREFIX = 'StyledSelect';
const classes = {
  select: `${PREFIX}-select`,
  selectIcon: `${PREFIX}-select__icon`,
  menuPopoverRoot: `${PREFIX}-popover`,
  menuPaper: `${PREFIX}-menuPaper`,
  menuList: `${PREFIX}-menuList`,
  helperText: `${PREFIX}-helperText`
};

// eslint-disable-next-line react/prop-types
const SelectInput = forwardRef(({ inputVariant, ...props }, ref) => (
  <Input {...props} variant={inputVariant} ref={ref} />
));

const Select = (props) => {
  const {
    analyticParams,
    className,
    children,
    containerProps,
    emptyOption,
    error,
    hint,
    helperTextProps,
    id,
    inputProps,
    inputVariant,
    InputComponentProps,
    label,
    labelProps,
    MenuProps,
    modalRootProps,
    modalClassName,
    options,
    required,
    testID,
    withEmptyOption,
    withHelperText,
    ...selectProps
  } = props;

  const isError = !!error;

  const labelId = id ? `${id}-label` : selectProps.labelId;
  const inputId = id ? `${id}-input` : selectProps.inputId;
  const helperId = id ? `${id}-helper` : selectProps.helperId;

  const menuProps = {
    variant: 'selectedMenu',
    // eslint-disable-next-line no-use-before-define
    component: StyledMenuModalRoot,
    classes: {
      paper: classes.menuPaper,
      list: classes.menuList
    },
    PaperProps: {
      style: { maxHeight: 8 * 47 }
    },
    // Popover props
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'center'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'center'
    },
    // Modal props
    slotProps: {
      root: {
        className: modalClassName,
        ...modalRootProps
      }
    },
    ...MenuProps
  };

  const renderOptions = () => {
    if (selectProps.native) {
      return map(options, ({ value: optionValue, label: optionLabel, ...rest }, i) => (
        <option key={`option__${i}`} value={optionValue} {...rest}>
          {optionLabel}
        </option>
      ));
    }
    return map(options, ({ value: optionValue, label: optionLabel, ...rest }, i) => (
      <MenuItem key={`option__${i}`} value={optionValue} {...rest}>
        {optionLabel}
      </MenuItem>
    ));
  };

  const renderEmptyOption = () =>
    selectProps.native ? (
      <option value="" aria-label="none" className="hidden" />
    ) : (
      <MenuItem value="" className="hidden" />
    );

  return (
    <FormControl error={isError} className={className} {...containerProps}>
      <MuiSelect
        // variant="standard"
        id={id}
        labelId={labelId}
        required={required}
        input={
          <SelectInput
            label={label}
            inputVariant={inputVariant}
            inputProps={{ id: inputId, ...inputProps }}
            InputLabelProps={{ id: labelId, ...labelProps }}
            FormControlProps={{
              // required,
              error: isError,
              ...(InputComponentProps.FormControlProps || {})
            }}
            analyticParams={analyticParams}
            testID={testID}
            {...InputComponentProps}
          />
        }
        IconComponent={MdExpandMore}
        MenuProps={menuProps}
        classes={{
          select: classes.select,
          icon: classes.selectIcon
        }}
        {...selectProps}
      >
        {withEmptyOption ? renderEmptyOption() : emptyOption}
        {options?.length ? renderOptions() : children}
      </MuiSelect>

      {withHelperText && (hint || error) && (
        <FormHelperText
          id={helperId}
          // error={isError}
          className={classes.helperText}
          {...helperTextProps}
        >
          {hint || error}
        </FormHelperText>
      )}
    </FormControl>
  );
}

const StyledSelect = styled(Select)(({ theme }) => ({
  [`& .${classes.select}`]: {
    '&.MuiSelect-select.StyledInput-inputBase__input': {
      minHeight: 'auto'
    },
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  [`& .${classes.selectIcon}`]: {
    right: 0,
    color: theme.palette.common.white
  },
  [`& .${classes.helperText}`]: {
    marginTop: 6,
    fontSize: 14,
    fontWeight: 'bold',
    lineHeight: '17px'
  }
}));

const StyledMenuModalRoot = styled(
  // eslint-disable-next-line react/prop-types
  forwardRef(({ className, children }, ref) => (
    <div ref={ref} className={className}>
      {children}
    </div>
  ))
)(({ theme }) => ({
  [`& .${classes.menuPaper}`]: {
    display: 'flex',
    borderRadius: 8,
    border: 'none',
    backgroundColor: theme.palette.grey['800']
  },
  [`& .${classes.menuList}`]: {
    minWidth: 300,
    overflow: 'auto',
    flex: 1,
    padding: 0,
    '& > option, li': {
      padding: 10,
      borderBottom: `1px solid ${theme.palette.grey['600']}`,
      borderColor: '#312E38', // @TODO: get from theme
      cursor: 'pointer',
      transition: `.25s ease background-color`,
      fontWeight: 700,
      fontSize: 18,
      whiteSpace: 'normal',
      color: theme.palette.common.white,
      '&.hidden': {
        display: 'none'
      },
      '&:hover, &.Mui-selected': {
        backgroundColor: theme.palette.primary.light
      }
    }
  }
}));

Select.propTypes = {
  analyticParams: PT.shape({
    trigger: PT.oneOf(['focus']),
    key: PT.string,
    params: PT.objectOf(PT.any)
  }),
  children: PT.node,
  className: PT.string,
  containerProps: PT.objectOf(PT.any),
  error: PT.oneOfType([PT.string, PT.node]),
  emptyOption: PT.node,
  hint: PT.string,
  helperTextProps: PT.objectOf(PT.any),
  id: PT.oneOfType([PT.string, PT.number]),
  inputVariant: PT.string,
  InputComponentProps: PT.objectOf(PT.any),
  inputProps: PT.objectOf(PT.any),
  labelProps: PT.objectOf(PT.any),
  label: PT.oneOfType([PT.string, PT.node]),
  MenuProps: PT.objectOf(PT.any),
  modalRootProps: PT.objectOf(PT.any),
  modalClassName: PT.string,
  options: PT.arrayOf(
    PT.shape({
      label: PT.string,
      value: PT.oneOfType([PT.string, PT.number])
    })
  ),
  required: PT.bool,
  testID: PT.string,
  withEmptyOption: PT.bool,
  withHelperText: PT.bool
};

Select.defaultProps = {
  analyticParams: { key: '', trigger: 'focus' },
  children: null,
  className: '',
  containerProps: {},
  error: '',
  emptyOption: null,
  id: '',
  InputComponentProps: {},
  hint: '',
  helperTextProps: {},
  inputProps: {},
  inputVariant: 'textfield',
  labelProps: {},
  label: '',
  MenuProps: {},
  modalRootProps: {},
  modalClassName: '',
  options: [],
  required: false,
  testID: '',
  withEmptyOption: false,
  withHelperText: false
};

export default StyledSelect;
